<template>
  <div class="diaporama" :style="overlayBgStyle">
    <div class="closeButtons">
      <q-btn icon="close" flat round dense @click="$emit('closeDiaporama')" />
      <ReturnHomeButton @click="$emit('closeAllDialogs')" />
    </div>

    <main
      class="immersive-slider"
      @pointerdown="handlePointerDown"
      @pointerup="handlePointerUp"
      @pointercancel="resetPointer"
    >
      <div v-if="loading" class="state-panel">Chargement...</div>
      <div v-else-if="resources.length === 0" class="state-panel">Aucune ressource dans le diaporama</div>

      <template v-else>
        <transition name="slide-fade" mode="out-in">
          <section :key="activeIndex" class="slide">
            <img
              v-if="activeType === 'image' && activeUrl"
              :src="activeUrl"
              :alt="activeResource.title || activeResource.name"
            />
            <video
              v-else-if="activeType === 'video' && activeUrl"
              :src="activeUrl"
              autoplay
              muted
              loop
              playsinline
            />
            <div v-else class="file-slide">
              <q-icon :name="fileIcon(activeType)" />
              <span>{{ activeUrl === null ? 'Média indisponible' : (activeResource.title || activeResource.name || 'Chargement...') }}</span>
            </div>
          </section>
        </transition>

        <section v-if="activeResource.title" class="slide-copy">
          <h1>{{ activeResource.title }}</h1>
        </section>

        <button class="nav-zone nav-zone--prev" type="button" @click="previous" aria-label="Image précédente">
          <q-icon name="chevron_left" />
        </button>
        <button class="nav-zone nav-zone--next" type="button" @click="next" aria-label="Image suivante">
          <q-icon name="chevron_right" />
        </button>

        <div class="dots" aria-label="Navigation des images">
          <button
            v-for="(_, index) in resources"
            :key="index"
            type="button"
            :class="{ active: index === activeIndex }"
            @click="goTo(index)"
            :aria-label="`Image ${index + 1}`"
          ></button>
        </div>
      </template>
    </main>
  </div>
</template>

<script setup>
import axios from 'axios';
import { computed, defineEmits, defineProps, onMounted, onBeforeUnmount, ref } from 'vue';
import ReturnHomeButton from './ReturnHomeButton.vue';
import { useOverlayBg } from '../composables/useOverlayBg';

const props = defineProps(['blockDatas']);
defineEmits(['closeDiaporama', 'closeAllDialogs']);

const { overlayBgStyle } = useOverlayBg();

const resources = ref([]);
const loading = ref(true);
const activeIndex = ref(0);
const pointerStartX = ref(null);

const activeResource = computed(() => resources.value[activeIndex.value] || {});
const activeUrl = computed(() => activeResource.value.url ?? '');
const activeType = computed(() => getResourceType(activeResource.value));

const getResourceType = (resource) => {
  const name = resource?.name || '';
  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (/\.(mp4|avi|mov|wmv|webm|mkv)$/i.test(name)) return 'video';
  if (/\.(mp3|wav|ogg|m4a)$/i.test(name)) return 'audio';
  if (/\.(pdf|doc|docx)$/i.test(name)) return 'doc';
  return 'file';
};

const fileIcon = (type) => {
  if (type === 'audio') return 'audiotrack';
  if (type === 'doc') return 'description';
  return 'insert_drive_file';
};

// Lecture automatique : une diapositive toutes les DUREE_DIAPO ms. Un geste
// du visiteur (flèche, point, glissé) relance le compte à rebours, pour ne
// pas changer d'image juste après qu'il a choisi celle qu'il veut voir.
const DUREE_DIAPO = 5000;
let minuterie = null;

const arreterLecture = () => {
  clearInterval(minuterie);
  minuterie = null;
};

const demarrerLecture = () => {
  arreterLecture();
  if (resources.value.length > 1) {
    minuterie = setInterval(() => avancer(activeIndex.value + 1), DUREE_DIAPO);
  }
};

const avancer = (index) => {
  if (!resources.value.length) return;
  activeIndex.value = (index + resources.value.length) % resources.value.length;
};

const goTo = (index) => {
  avancer(index);
  demarrerLecture();
};

const next = () => goTo(activeIndex.value + 1);
const previous = () => goTo(activeIndex.value - 1);

const handlePointerDown = (event) => {
  pointerStartX.value = event.clientX;
};

const handlePointerUp = (event) => {
  if (pointerStartX.value === null) return;
  const delta = event.clientX - pointerStartX.value;
  if (Math.abs(delta) > 48) {
    delta < 0 ? next() : previous();
  }
  resetPointer();
};

const resetPointer = () => {
  pointerStartX.value = null;
};

const fetchResources = async () => {
  const associatedResources = props.blockDatas?.associatedResources || [];
  loading.value = true;

  const fetched = await Promise.all(
    associatedResources.map(async (resource, position) => {
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASEURL}/s3Resource/${resource.name}`);
        return {
          ...resource,
          url: response.data,
          sortIndex: Number.isFinite(Number(resource.index)) ? Number(resource.index) : position,
        };
      } catch (error) {
        return {
          ...resource,
          url: null,
          sortIndex: Number.isFinite(Number(resource.index)) ? Number(resource.index) : position,
        };
      }
    })
  );

  fetched.sort((a, b) => a.sortIndex - b.sortIndex);
  resources.value = fetched;
  loading.value = false;
  demarrerLecture();
};

onMounted(fetchResources);
onBeforeUnmount(arreterLecture);
</script>

<style scoped>
.diaporama {
  background-color: rgba(217, 217, 217, 1);
  position: fixed;
  inset: 0;
  z-index: 9999;
  width: 100%;
  height: 100%;
  overflow: hidden;
  animation: overlayFadeIn 200ms ease both;
}

.closeButtons {
  position: absolute;
  top: 16px;
  right: 16px;
  width: auto;
  margin: 0;
  display: flex;
  gap: 10px;
  z-index: 10001;
}

.immersive-slider {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  touch-action: pan-y;
}

.slide {
  position: absolute;
  inset: 0;
}

.slide img,
.slide video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  animation: slowScale 8s ease forwards;
}

.slide-copy {
  position: absolute;
  left: 64px;
  bottom: 40px;
  z-index: 2;
  max-width: 560px;
  padding: 10px 18px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.72);
  animation: copyIn 520ms ease both;
}

h1 {
  margin: 0;
  color: #1f1f1f;
  font-size: 28px;
  font-weight: 500;
  line-height: 1.2;
}

.nav-zone {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 3;
  display: grid;
  place-items: center;
  width: 18vw;
  min-width: 90px;
  border: 0;
  background: transparent;
  color: rgba(0, 0, 0, 0);
  cursor: pointer;
  font-size: 58px;
  transition: color 160ms ease, background 160ms ease;
}

.nav-zone:hover {
  color: rgba(0, 0, 0, 0.68);
  background: rgba(255, 255, 255, 0.14);
}

.nav-zone:active {
  color: #000;
}

.nav-zone--prev {
  left: 0;
}

.nav-zone--next {
  right: 0;
}

.dots {
  position: absolute;
  left: 50%;
  bottom: 24px;
  z-index: 4;
  display: flex;
  gap: 16px;
  transform: translateX(-50%);
}

.dots button {
  width: 16px;
  height: 16px;
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  opacity: 0.9;
  transition: transform 160ms ease, background 160ms ease, opacity 160ms ease;
}

.dots button.active {
  background: #1f1f1f;
  transform: scale(1.22);
  opacity: 1;
}

.state-panel {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  color: #333;
  font-size: 18px;
}

.file-slide {
  display: grid;
  place-items: center;
  gap: 18px;
  height: 100%;
  padding: 42px;
  text-align: center;
  color: #333;
}

.file-slide .q-icon {
  font-size: 80px;
  color: #555;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 260ms ease, transform 260ms ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: scale(1.015);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: scale(0.99);
}

@keyframes slowScale {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.04);
  }
}

@keyframes copyIn {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes overlayFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 780px) {
  .slide-copy {
    left: 24px;
    right: 24px;
    bottom: 64px;
    max-width: none;
  }

  h1 {
    font-size: 20px;
  }

  .nav-zone {
    width: 50%;
    min-width: 0;
  }

  .dots {
    gap: 12px;
  }

  .dots button {
    width: 14px;
    height: 14px;
  }
}
</style>
